import { PlaceLocationRecord } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from 'noddi-ui-web/src/base';
import type { MapLayerKey } from './constants';
import { MAP_LAYERS } from './constants';
import type { CenterCoordinates } from './NoddiLeafletMapContainer';
import { PlaceSearch, type PlaceSearchTranslations } from './PlaceSearch';
import type { MapCurrentLocationState } from './useMapCurrentLocation';

const LAYER_KEYS: MapLayerKey[] = ['streets', 'satellite', 'light'];

type Props = {
  centerCoordinates?: CenterCoordinates;
  showCurrentLocation: boolean;
  showLayerToggle: boolean;
  showPlaceSearch: boolean;
  activeLayer: MapLayerKey;
  locationState: MapCurrentLocationState;
  placeSearchTranslations?: PlaceSearchTranslations;
  fullScreen?: { isFullScreen: boolean; onToggle: () => void };
  onCenter: () => void;
  onLayerChange: (layer: MapLayerKey) => void;
  onPlaceClear: () => void;
  onPlaceSelected: (location: PlaceLocationRecord) => void;
  onCurrentLocation: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
};

const mapControlButtonClass =
  'rounded-lg border border-gray-300 bg-white p-2 shadow-md hover:bg-gray-50 transition-colors';

export const LeafletMapControls = ({
  centerCoordinates,
  showCurrentLocation,
  showLayerToggle,
  showPlaceSearch,
  activeLayer,
  locationState,
  placeSearchTranslations,
  fullScreen,
  onCenter,
  onLayerChange,
  onPlaceClear,
  onPlaceSelected,
  onCurrentLocation,
  onZoomIn,
  onZoomOut
}: Props) => (
  <>
    {showPlaceSearch && (
      <div className='absolute top-3 left-3 z-20'>
        <PlaceSearch onPlaceSelected={onPlaceSelected} onClear={onPlaceClear} translations={placeSearchTranslations} />
      </div>
    )}

    <div className='absolute right-3 bottom-5 z-20 flex flex-col gap-2'>
      {fullScreen && (
        <button
          type='button'
          onClick={fullScreen.onToggle}
          className={mapControlButtonClass}
          title={fullScreen.isFullScreen ? 'Exit full screen' : 'Full screen'}
          aria-label={fullScreen.isFullScreen ? 'Exit full screen' : 'Full screen'}
        >
          <NoddiIcon name={fullScreen.isFullScreen ? 'Minimize2' : 'Maximize2'} size='small' />
        </button>
      )}
      <div className='flex flex-col overflow-hidden rounded-lg border border-gray-300 shadow-md'>
        <button
          type='button'
          onClick={onZoomIn}
          className='border-b border-gray-300 bg-white p-2 transition-colors hover:bg-gray-50'
          title='Zoom in'
        >
          <NoddiIcon name='Plus' size='small' />
        </button>
        <button
          type='button'
          onClick={onZoomOut}
          className='bg-white p-2 transition-colors hover:bg-gray-50'
          title='Zoom out'
        >
          <NoddiIcon name='Minus' size='small' />
        </button>
      </div>

      {showCurrentLocation && (
        <button
          type='button'
          onClick={onCurrentLocation}
          className={cn(
            mapControlButtonClass,
            locationState === 'locating' && 'animate-pulse text-blue-500',
            locationState === 'active' && 'text-blue-500'
          )}
          title='My location'
          disabled={locationState === 'locating'}
        >
          <NoddiIcon name='LocateFixed' size='small' />
        </button>
      )}

      {showLayerToggle && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type='button' className={mapControlButtonClass}>
              <NoddiIcon name='Layers' size='small' />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side='left' align='start'>
            <DropdownMenuRadioGroup value={activeLayer} onValueChange={(value) => onLayerChange(value as MapLayerKey)}>
              {LAYER_KEYS.map((key) => (
                <DropdownMenuRadioItem key={key} value={key}>
                  {MAP_LAYERS[key].label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>

    {centerCoordinates && (
      <div className='absolute bottom-5 left-3 z-20'>
        <button type='button' onClick={onCenter} className={mapControlButtonClass} title='Center map'>
          <NoddiIcon name='Target' size='small' />
        </button>
      </div>
    )}
  </>
);
